import React, { useEffect, useRef } from 'react';
import styled, { css } from 'styled-components';
import { useTranslation } from 'react-i18next';
import { useLanguage } from '../../contexts/LanguageContext';
import { useTheme } from '../../contexts/ThemeContext';

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0,0,0,0.45);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`; 

const Modal = styled.div`
  width: 90%;
  max-width: 520px;
  border-radius: 16px;
  box-shadow: 0 10px 30px rgba(0,0,0,0.2);
  ${(props) => props.$theme === 'dark' ? css`
    background: #1f2937; /* gray-800 */
    color: #f9fafb; /* gray-50 */
    border: 1px solid #374151; /* gray-700 */
  ` : css`
    background: #ffffff;
    color: #111827; /* gray-900 */
    border: 1px solid #e5e7eb; /* gray-200 */
  `}
`; 

const Header = styled.div`
  padding: 16px 20px;
  border-bottom: 1px solid rgba(229, 231, 235, 0.6);
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const Title = styled.h2`
  font-size: 18px;
  font-weight: 600;
  margin: 0;
`;

const Body = styled.div`
  padding: 16px 20px;
`;

const Footer = styled.div`
  padding: 16px 20px;
  display: flex;
  gap: 12px;
  justify-content: flex-end;
`;

const Button = styled.button`
  border-radius: 10px;
  padding: 10px 16px;
  font-weight: 500;
  transition: background-color 0.2s ease, opacity 0.2s ease;
  ${(props) => props.$variant === 'primary' && css`
    background: #dc2626; /* red-600 */
    color: #ffffff;
    &:hover { background: #b91c1c; }
    &:disabled { opacity: 0.6; cursor: not-allowed; }
  `}
  ${(props) => props.$variant === 'secondary' && css`
    background: transparent;
    border: 1px solid #e5e7eb; /* gray-200 */
    color: inherit;
    &:hover { background: rgba(0,0,0,0.05); }
  `}
`;

const Row = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;

  @media (max-width: 480px) {
    grid-template-columns: 1fr;
  }
`;

const Box = styled.div`
  border-radius: 12px;
  padding: 12px;
  ${(props) => props.$theme === 'dark' ? css`
    background: #111827; /* gray-900 */
    border: 1px solid #1f2937; /* gray-800 */
  ` : css`
    background: #f9fafb; /* gray-50 */
    border: 1px solid #e5e7eb; /* gray-200 */
  `}
`;

/**
 * ConfirmDeleteModal
 * Props:
 * - open: boolean
 * - onConfirm(): void
 * - onCancel(): void
 * - loading: boolean (show loading state on Yes button)
 * - recordLabel?: string (optional label for clarity)
 */
const ConfirmDeleteModal = ({ open, onConfirm, onCancel, loading = false, recordLabel }) => {
  const { t, i18n } = useTranslation();
  const { isRTL } = useLanguage();
  const { theme } = useTheme();
  const yesRef = useRef(null);

  useEffect(() => {
    if (open && yesRef.current) {
      yesRef.current.focus();
    }
  }, [open]);

  if (!open) return null;

  const tAr = i18n.getFixedT('ar');
  const tEn = i18n.getFixedT('en');

  const arabicText = recordLabel
    ? `${tAr('common.confirmMessage')}\n${recordLabel}`
    : tAr('common.confirmMessage');
  const englishText = recordLabel
    ? `${tEn('common.confirmMessage')}\n${recordLabel}`
    : tEn('common.confirmMessage');

  const handleKeyDown = (e) => {
    if (e.key === 'Escape') onCancel?.();
  };

  return (
    <Overlay aria-hidden={!open}>
      <Modal role="dialog" aria-modal="true" aria-labelledby="confirm-delete-title" aria-describedby="confirm-delete-description" onKeyDown={handleKeyDown} $theme={theme} dir={isRTL ? 'rtl' : 'ltr'}>
        <Header>
          <Title id="confirm-delete-title">{t('common.confirmAction')}</Title>
          <button aria-label={t('common.close')} onClick={onCancel} style={{ background: 'transparent', border: 'none', fontSize: 18 }}>×</button>
        </Header>
        <Body>
          <Row>
            <Box $theme={theme} style={{ textAlign: 'right' }}>
              <p id="confirm-delete-description" style={{ margin: 0, whiteSpace: 'pre-line' }}>{arabicText}</p>
            </Box>
            <Box $theme={theme} style={{ textAlign: 'left' }}>
              <p style={{ margin: 0, whiteSpace: 'pre-line' }}>{englishText}</p>
            </Box>
          </Row>
        </Body>
        <Footer>
          <Button ref={yesRef} onClick={onConfirm} aria-label={t('common.yes')} $variant="primary" disabled={loading}>
            {loading ? t('common.loading') : t('common.yes')}
          </Button>
          <Button onClick={onCancel} aria-label={t('common.no')} $variant="secondary">
            {t('common.no')}
          </Button>
        </Footer>
      </Modal>
    </Overlay>
  );
};

export default ConfirmDeleteModal;